<script setup>
import { useApi } from '@/utils/api'
import { useApiRequest } from '@/composables/useApiRequest'
import { useAbility } from '@casl/vue'
import { useFormat } from '@/composables/useFormat'
import { useStockUnitConversion } from '@/composables/useStockUnitConversion'
import { requiredValidator, betweenValidator } from '@core/utils/validators'

definePage({
  meta: {
    layout: 'default',
    action: 'read',
    subject: 'Inventory',
  },
})

const route = useRoute()
const ability = useAbility()
const { request, loading } = useApiRequest()
const { formatRupiah, formatNumber, formatDate } = useFormat()
const { purchasableUnitsFor, toBaseQuantity, computeUnitCost } = useStockUnitConversion()

const ingredientId = route.params.id

// ─── State ────────────────────────────────────────────────────────────────────
const ingredient = ref(null)
const movements = ref([])
const movementTotal = ref(0)
const pageLoading = ref(true)
const units = ref([])

const movementOptions = ref({
  page: 1,
  itemsPerPage: 20,
})
const movementTypeFilter = ref(null)
const dateFrom = ref(null)
const dateTo = ref(null)

// Dialog: Initial Stock — "Smart HPP Calculator", sama persis dengan section
// Stok Awal di halaman Tambah Bahan Baku (reuse useStockUnitConversion, satu
// implementasi konversi/perhitungan HPP untuk kedua tempat).
const initialDialog = ref(false)
const initialForm = ref({ purchaseUnitId: null, quantity: null, totalPrice: null, notes: '' })
const initialFormRef = ref()
const { loading: initialLoading, errors: initialErrors, request: initialRequest } = useApiRequest()

const purchasableUnits = computed(() => purchasableUnitsFor(units.value, ingredient.value?.unit?.id))

const selectedPurchaseUnit = computed(() =>
  purchasableUnits.value.find(u => u.id === initialForm.value.purchaseUnitId),
)

const initialBaseQuantity = computed(() =>
  toBaseQuantity(initialForm.value.quantity, selectedPurchaseUnit.value),
)

const initialUnitCost = computed(() =>
  computeUnitCost(initialForm.value.totalPrice, initialBaseQuantity.value),
)

// Dialog: Adjust Stock
const adjustDialog = ref(false)
const adjustForm = ref({ new_stock: null, reason: '' })
const adjustFormRef = ref()
const { loading: adjustLoading, errors: adjustErrors, request: adjustRequest } = useApiRequest()

// ─── Movement Types ─────────────────────────────────────────────────────────────
const movementTypes = [
  { title: 'Semua Tipe', value: null },
  { title: 'Stok Awal', value: 'initial' },
  { title: 'Pembelian', value: 'purchase' },
  { title: 'Penjualan', value: 'sale' },
  { title: 'Penyesuaian', value: 'adjustment' },
  { title: 'Stok Opname', value: 'opname' },
  { title: 'Pembuangan', value: 'waste' },
  { title: 'Retur', value: 'return' },
]

const movementTypeColor = {
  initial: 'primary',
  purchase: 'success',
  sale: 'warning',
  adjustment: 'info',
  opname: 'secondary',
  waste: 'error',
  return: 'teal',
  void_sale: 'orange',
  void_purchase: 'deep-orange',
}

// ─── Movement Table Headers ────────────────────────────────────────────────────
const movementHeaders = [
  { title: 'Tanggal', key: 'created_at', width: '160px' },
  { title: 'Tipe', key: 'type', width: '130px', sortable: false },
  { title: 'Qty', key: 'quantity', width: '110px', align: 'end' },
  { title: 'Stok Sebelum', key: 'stock_before', width: '130px', align: 'end' },
  { title: 'Stok Sesudah', key: 'stock_after', width: '130px', align: 'end' },
  { title: 'HPP / Unit', key: 'unit_cost', width: '120px', align: 'end' },
  { title: 'Catatan', key: 'notes', sortable: false },
  { title: 'Oleh', key: 'created_by', width: '120px', sortable: false },
]

// ─── Fetch ─────────────────────────────────────────────────────────────────────
const fetchIngredient = async () => {
  const response = await useApi(`/api/v1/ingredients/${ingredientId}`).get()
  ingredient.value = response?.data ?? response
}

const fetchUnits = async () => {
  const response = await useApi('/api/v1/units', { query: { per_page: 100 } }).get()
  units.value = response?.data ?? []
}

const fetchMovements = async () => {
  const query = {
    page: movementOptions.value.page,
    per_page: movementOptions.value.itemsPerPage,
  }
  if (movementTypeFilter.value) query.type = movementTypeFilter.value
  if (dateFrom.value) query.date_from = dateFrom.value
  if (dateTo.value) query.date_to = dateTo.value

  const response = await useApi(`/api/v1/ingredients/${ingredientId}/movements`, { query }).get()
  const data = response?.data ?? response

  movements.value = data?.data ?? []
  movementTotal.value = data?.pagination?.total ?? 0
}

const initPage = async () => {
  pageLoading.value = true
  try {
    await Promise.all([fetchIngredient(), fetchMovements(), fetchUnits()])
  } finally {
    pageLoading.value = false
  }
}

// ─── Initial Stock Submit ──────────────────────────────────────────────────────
const submitInitialStock = async () => {
  const { valid } = await initialFormRef.value?.validate()
  if (!valid) return

  // quantity/unit_cost dikirim SUDAH dalam satuan dasar (dikonversi di FE) —
  // endpoint ini sama persis dengan yang dipakai section Stok Awal di
  // halaman Tambah Bahan Baku, tidak ada logic stok terpisah.
  const res = await initialRequest(() =>
    useApi(`/api/v1/ingredients/${ingredientId}/initial-stock`).post({
      body: {
        quantity: initialBaseQuantity.value,
        unit_cost: initialUnitCost.value,
        notes: initialForm.value.notes || undefined,
      },
    }),
  )

  if (res) {
    initialDialog.value = false
    initialForm.value = { purchaseUnitId: null, quantity: null, totalPrice: null, notes: '' }
    await Promise.all([fetchIngredient(), fetchMovements()])
  }
}

// ─── Adjust Stock Submit ───────────────────────────────────────────────────────
const submitAdjust = async () => {
  const { valid } = await adjustFormRef.value?.validate()
  if (!valid) return

  const res = await adjustRequest(() =>
    useApi(`/api/v1/ingredients/${ingredientId}/adjust`).post({
      body: adjustForm.value,
    }),
  )

  if (res) {
    adjustDialog.value = false
    adjustForm.value = { new_stock: null, reason: '' }
    await Promise.all([fetchIngredient(), fetchMovements()])
  }
}

// ─── Open Dialogs ──────────────────────────────────────────────────────────────
const openAdjustDialog = () => {
  adjustForm.value = {
    new_stock: ingredient.value?.current_stock ?? 0,
    reason: '',
  }
  adjustDialog.value = true
}

// ─── Lifecycle ─────────────────────────────────────────────────────────────────
onMounted(initPage)

watch(
  [movementOptions, movementTypeFilter, dateFrom, dateTo],
  fetchMovements,
  { deep: true },
)
</script>

<template>
  <div>
    <!-- Back Button -->
    <VBtn
      variant="text"
      prepend-icon="ri-arrow-left-line"
      :to="{ name: 'inventory-ingredients' }"
      class="mb-4 px-0"
    >
      Kembali ke Daftar
    </VBtn>

    <!-- Loading skeleton -->
    <VRow v-if="pageLoading">
      <VCol cols="12">
        <VCard><VCardText><VSkeleton type="article" /></VCardText></VCard>
      </VCol>
    </VRow>

    <template v-else-if="ingredient">
      <!-- Header Card -->
      <VCard class="mb-6">
        <VCardText>
          <VRow align="center">
            <!-- Info Bahan -->
            <VCol cols="12" md="6">
              <div class="d-flex align-center gap-3">
                <VAvatar color="primary" variant="tonal" size="52" rounded="lg">
                  <VIcon icon="ri-archive-drawer-line" size="28" />
                </VAvatar>
                <div>
                  <div class="d-flex align-center gap-2">
                    <h5 class="text-h5 font-weight-semibold">{{ ingredient.name }}</h5>
                    <VChip
                      :color="ingredient.is_active ? 'success' : 'error'"
                      size="small"
                      variant="tonal"
                    >
                      {{ ingredient.is_active ? 'Aktif' : 'Nonaktif' }}
                    </VChip>
                    <VChip
                      v-if="ingredient.is_low_stock"
                      color="warning"
                      size="small"
                      variant="tonal"
                    >
                      <VIcon start icon="ri-alert-line" size="12" />
                      Stok Rendah
                    </VChip>
                  </div>
                  <div class="text-body-2 text-medium-emphasis mt-1">
                    <span v-if="ingredient.sku">SKU: {{ ingredient.sku }}</span>
                    <span v-if="ingredient.sku && ingredient.category"> · </span>
                    <span v-if="ingredient.category">{{ ingredient.category.name }}</span>
                  </div>
                </div>
              </div>
            </VCol>

            <!-- Action Buttons -->
            <VCol cols="12" md="6" class="d-flex justify-md-end gap-2 flex-wrap">
              <!-- Set Initial Stock (hanya jika stok = 0) -->
              <VBtn
                v-if="ability.can('update', 'Inventory') && ingredient.current_stock === 0"
                color="primary"
                variant="tonal"
                prepend-icon="ri-stock-line"
                @click="initialDialog = true"
              >
                Set Stok Awal
              </VBtn>

              <!-- Adjust Stock -->
              <VBtn
                v-if="ability.can('update', 'Inventory') && ingredient.current_stock !== 0"
                color="info"
                variant="tonal"
                prepend-icon="ri-equalizer-line"
                @click="openAdjustDialog"
              >
                Sesuaikan Stok
              </VBtn>

              <!-- Edit -->
              <VBtn
                v-if="ability.can('update', 'Inventory')"
                variant="tonal"
                prepend-icon="ri-edit-line"
                :to="{ name: 'inventory-ingredients-id-edit', params: { id: ingredientId } }"
              >
                Edit
              </VBtn>
            </VCol>
          </VRow>
        </VCardText>

        <VDivider />

        <!-- Stats Row -->
        <VCardText>
          <VRow>
            <VCol cols="6" md="3">
              <div class="text-medium-emphasis text-body-2 mb-1">Stok Saat Ini</div>
              <div
                class="text-h5 font-weight-bold"
                :class="ingredient.is_low_stock ? 'text-warning' : 'text-high-emphasis'"
              >
                {{ formatNumber(ingredient.current_stock) }}
                <span class="text-body-2 font-weight-regular ms-1">{{ ingredient.unit?.symbol }}</span>
              </div>
            </VCol>
            <VCol cols="6" md="3">
              <div class="text-medium-emphasis text-body-2 mb-1">Minimum Stok</div>
              <div class="text-h5 font-weight-bold">
                {{ formatNumber(ingredient.minimum_stock) }}
                <span class="text-body-2 font-weight-regular ms-1">{{ ingredient.unit?.symbol }}</span>
              </div>
            </VCol>
            <VCol cols="6" md="3">
              <div class="text-medium-emphasis text-body-2 mb-1">HPP Rata-rata (WAC)</div>
              <div class="text-h5 font-weight-bold">{{ formatRupiah(ingredient.average_cost) }}</div>
            </VCol>
            <VCol cols="6" md="3">
              <div class="text-medium-emphasis text-body-2 mb-1">Nilai Stok</div>
              <div class="text-h5 font-weight-bold text-primary">
                {{ formatRupiah(ingredient.stock_value) }}
              </div>
            </VCol>
          </VRow>
        </VCardText>
      </VCard>

      <!-- Riwayat Stok -->
      <VCard>
        <VCardTitle class="pa-6 pb-3 text-h6">Riwayat Pergerakan Stok</VCardTitle>
        <VDivider />

        <!-- Filter Movements -->
        <VCardText class="pb-0">
          <VRow align="center" class="gap-y-2">
            <VCol cols="12" sm="4" md="3">
              <VSelect
                v-model="movementTypeFilter"
                :items="movementTypes"
                item-title="title"
                item-value="value"
                label="Filter Tipe"
                density="compact"
                hide-details
              />
            </VCol>
            <VCol cols="6" sm="3" md="2">
              <VTextField
                v-model="dateFrom"
                label="Dari Tanggal"
                type="date"
                density="compact"
                hide-details
              />
            </VCol>
            <VCol cols="6" sm="3" md="2">
              <VTextField
                v-model="dateTo"
                label="Sampai Tanggal"
                type="date"
                density="compact"
                hide-details
              />
            </VCol>
            <VCol cols="auto">
              <VBtn
                variant="text"
                size="small"
                prepend-icon="ri-refresh-line"
                @click="movementTypeFilter = null; dateFrom = null; dateTo = null"
              >
                Reset
              </VBtn>
            </VCol>
          </VRow>
        </VCardText>

        <!-- Movements Table -->
        <VDataTableServer
          v-model:options="movementOptions"
          :headers="movementHeaders"
          :items="movements"
          :loading="loading"
          :items-length="movementTotal"
          hover
          class="text-no-wrap mt-2"
        >
          <!-- Tanggal -->
          <template #item.created_at="{ item }">
            <span class="text-body-2">{{ formatDate(item.created_at) }}</span>
          </template>

          <!-- Tipe -->
          <template #item.type="{ item }">
            <VChip
              :color="movementTypeColor[item.type] ?? 'default'"
              size="small"
              variant="tonal"
            >
              {{ item.type_label }}
            </VChip>
          </template>

          <!-- Quantity -->
          <template #item.quantity="{ item }">
            <span
              class="font-weight-medium"
              :class="item.is_incoming ? 'text-success' : 'text-error'"
            >
              {{ item.is_incoming ? '+' : '' }}{{ formatNumber(item.quantity) }}
              <span class="text-caption ms-1">{{ ingredient.unit?.symbol }}</span>
            </span>
          </template>

          <!-- Stock Before -->
          <template #item.stock_before="{ item }">
            <span class="text-medium-emphasis">
              {{ formatNumber(item.stock_before) }}
            </span>
          </template>

          <!-- Stock After -->
          <template #item.stock_after="{ item }">
            <span class="font-weight-medium">
              {{ formatNumber(item.stock_after) }}
            </span>
          </template>

          <!-- Unit Cost -->
          <template #item.unit_cost="{ item }">
            <span v-if="item.unit_cost > 0">{{ formatRupiah(item.unit_cost) }}</span>
            <span v-else class="text-disabled">-</span>
          </template>

          <!-- Notes -->
          <template #item.notes="{ item }">
            <span class="text-body-2 text-medium-emphasis">
              {{ item.notes ?? (item.reference_type ? `Ref: ${item.reference_type} #${item.reference_id}` : '-') }}
            </span>
          </template>

          <!-- Created By -->
          <template #item.created_by="{ item }">
            <span class="text-body-2">{{ item.created_by?.name ?? 'Sistem' }}</span>
          </template>

          <!-- No Data -->
          <template #no-data>
            <div class="text-center pa-8">
              <VIcon icon="ri-history-line" size="48" color="grey-lighten-1" class="mb-3" />
              <p class="text-body-2 text-medium-emphasis">Belum ada riwayat pergerakan stok</p>
            </div>
          </template>
        </VDataTableServer>
      </VCard>
    </template>

    <!-- ─── Dialog: Set Stok Awal ─────────────────────────────────────────────── -->
    <VDialog v-model="initialDialog" max-width="480">
      <VCard>
        <VCardTitle class="pa-6 pb-3 text-h6">
          <VIcon icon="ri-stock-line" class="me-2" />
          Set Stok Awal
        </VCardTitle>
        <VDivider />

        <VForm ref="initialFormRef" @submit.prevent="submitInitialStock">
          <VCardText class="pt-4">
            <VAlert type="info" variant="tonal" density="compact" class="mb-4">
              Stok awal hanya bisa diset jika stok masih 0. Operasi ini tidak dapat diulang.
            </VAlert>

            <VRow>
              <VCol cols="12" sm="6">
                <VTextField
                  v-model.number="initialForm.quantity"
                  label="Jumlah Pembelian *"
                  type="number"
                  min="0.0001"
                  step="any"
                  :rules="[requiredValidator, v => v > 0 || 'Jumlah harus lebih dari 0']"
                  :error-messages="initialErrors.quantity"
                />
              </VCol>
              <VCol cols="12" sm="6">
                <VSelect
                  v-model="initialForm.purchaseUnitId"
                  :items="purchasableUnits"
                  item-title="name"
                  item-value="id"
                  label="Satuan"
                >
                  <template #item="{ props, item }">
                    <VListItem v-bind="props">
                      <template #append>
                        <VChip size="x-small" color="primary" variant="tonal">
                          {{ item.raw.symbol }}
                        </VChip>
                      </template>
                    </VListItem>
                  </template>
                </VSelect>
              </VCol>
              <VCol cols="12">
                <VTextField
                  v-model.number="initialForm.totalPrice"
                  label="Total Harga Pembelian *"
                  type="number"
                  min="0"
                  step="any"
                  prefix="Rp"
                  :rules="[requiredValidator, v => v >= 0 || 'Harga tidak boleh negatif']"
                  :error-messages="initialErrors.unit_cost"
                />
              </VCol>
              <VCol cols="12">
                <VTextField
                  :model-value="formatRupiah(initialUnitCost)"
                  :label="`HPP per ${ingredient?.unit?.symbol ?? 'satuan'} (Otomatis)`"
                  readonly
                  variant="filled"
                  hint="Terhitung otomatis: Total Harga ÷ Jumlah (dikonversi ke satuan dasar)"
                  persistent-hint
                />
              </VCol>
              <VCol cols="12">
                <VTextarea
                  v-model="initialForm.notes"
                  label="Catatan (opsional)"
                  rows="2"
                  placeholder="contoh: Stok awal dari supplier XYZ"
                  :error-messages="initialErrors.notes"
                />
              </VCol>
            </VRow>

            <!-- Preview -->
            <VAlert
              v-if="initialForm.quantity && initialForm.totalPrice"
              type="success"
              variant="tonal"
              density="compact"
              class="mt-2"
            >
              Nilai stok awal: <strong>{{ formatRupiah(initialForm.totalPrice) }}</strong>
              ({{ formatNumber(initialBaseQuantity) }} {{ ingredient?.unit?.symbol }})
            </VAlert>
          </VCardText>

          <VDivider />
          <VCardActions class="pa-4">
            <VSpacer />
            <VBtn variant="text" @click="initialDialog = false">Batal</VBtn>
            <VBtn
              type="submit"
              color="primary"
              :loading="initialLoading"
            >
              Set Stok Awal
            </VBtn>
          </VCardActions>
        </VForm>
      </VCard>
    </VDialog>

    <!-- ─── Dialog: Penyesuaian Stok ──────────────────────────────────────────── -->
    <VDialog v-model="adjustDialog" max-width="480">
      <VCard>
        <VCardTitle class="pa-6 pb-3 text-h6">
          <VIcon icon="ri-equalizer-line" class="me-2" />
          Penyesuaian Stok
        </VCardTitle>
        <VDivider />

        <VForm ref="adjustFormRef" @submit.prevent="submitAdjust">
          <VCardText class="pt-4">
            <VAlert type="warning" variant="tonal" density="compact" class="mb-4">
              Penyesuaian stok akan dicatat sebagai <strong>adjustment</strong> dengan selisih dari stok saat ini.
              Pastikan sudah melakukan penghitungan fisik.
            </VAlert>

            <VRow>
              <VCol cols="12">
                <VTextField
                  v-model.number="adjustForm.new_stock"
                  label="Stok Baru (Hasil Hitung Fisik) *"
                  type="number"
                  min="0"
                  step="0.01"
                  :suffix="ingredient?.unit?.symbol"
                  :rules="[requiredValidator, v => v >= 0 || 'Stok tidak boleh negatif']"
                  :error-messages="adjustErrors.new_stock"
                  :hint="`Stok saat ini: ${formatNumber(ingredient?.current_stock ?? 0)} ${ingredient?.unit?.symbol ?? ''}`"
                  persistent-hint
                />
              </VCol>

              <VCol cols="12">
                <VTextField
                  v-model="adjustForm.reason"
                  label="Alasan Penyesuaian *"
                  placeholder="contoh: Koreksi setelah hitung fisik bulanan"
                  :rules="[requiredValidator]"
                  :error-messages="adjustErrors.reason"
                />
              </VCol>
            </VRow>

            <!-- Preview Selisih -->
            <VAlert
              v-if="adjustForm.new_stock !== null && ingredient"
              :type="adjustForm.new_stock > ingredient.current_stock ? 'success' : adjustForm.new_stock < ingredient.current_stock ? 'error' : 'info'"
              variant="tonal"
              density="compact"
              class="mt-2"
            >
              Selisih:
              <strong>
                {{ adjustForm.new_stock > ingredient.current_stock ? '+' : '' }}
                {{ formatNumber(adjustForm.new_stock - ingredient.current_stock) }}
                {{ ingredient.unit?.symbol }}
              </strong>
              <span v-if="adjustForm.new_stock === ingredient.current_stock">
                — Tidak ada perubahan stok
              </span>
            </VAlert>
          </VCardText>

          <VDivider />
          <VCardActions class="pa-4">
            <VSpacer />
            <VBtn variant="text" @click="adjustDialog = false">Batal</VBtn>
            <VBtn
              type="submit"
              color="warning"
              :loading="adjustLoading"
            >
              Simpan Penyesuaian
            </VBtn>
          </VCardActions>
        </VForm>
      </VCard>
    </VDialog>
  </div>
</template>
